import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthGate } from './components/auth/AuthGate';
import { AppShell } from './components/layout/AppShell';
import { useAuth } from './lib/auth';
import { queryClient } from './lib/queries';

import { OverviewPage } from './pages/OverviewPage';
import { InboxPage } from './pages/InboxPage';
import { UsersPage } from './pages/UsersPage';
import { UserDetailPage } from './pages/UserDetailPage';
import { SessionsPage } from './pages/SessionsPage';
import { SessionDetailPage } from './pages/SessionDetailPage';
import { RevenuePage } from './pages/RevenuePage';
import { GrowthPage } from './pages/GrowthPage';
import { ReferralsPage } from './pages/ReferralsPage';
import { EventsPage } from './pages/EventsPage';
import { AlertsPage } from './pages/AlertsPage';
import { EmailPage } from './pages/EmailPage';
import { PsychometricsPage } from './pages/PsychometricsPage';
import { VyoPage } from './pages/VyoPage';
import { LearnPage } from './pages/LearnPage';
import { OutcomesPage } from './pages/OutcomesPage';
import { CataloguePage } from './pages/CataloguePage';
import { PacksPage } from './pages/PacksPage';
import { CompliancePage } from './pages/CompliancePage';
import { DeletionDetailPage } from './pages/DeletionDetailPage';
import { OpsPage } from './pages/OpsPage';
import { PsychometricAuthoringPage } from './pages/PsychometricAuthoringPage';

const InnerApp: React.FC = () => {
  const { user, signOut } = useAuth();

  return (
    <AppShell userEmail={user?.email ?? null} onSignOut={signOut}>
      <Routes>
        <Route path="/"            element={<OverviewPage />} />
        <Route path="/inbox"       element={<InboxPage />} />
        <Route path="/users"       element={<UsersPage />} />
        <Route path="/users/:id"   element={<UserDetailPage />} />
        <Route path="/sessions"    element={<SessionsPage />} />
        <Route path="/sessions/:id" element={<SessionDetailPage />} />
        <Route path="/revenue"     element={<RevenuePage />} />
        <Route path="/growth"      element={<GrowthPage />} />
        <Route path="/referrals"   element={<ReferralsPage />} />
        <Route path="/events"      element={<EventsPage />} />
        <Route path="/alerts"      element={<AlertsPage />} />
        <Route path="/email"       element={<EmailPage />} />
        <Route path="/psychometrics" element={<PsychometricsPage />} />
        <Route path="/vyo"         element={<VyoPage />} />
        <Route path="/learn"       element={<LearnPage />} />
        <Route path="/outcomes"    element={<OutcomesPage />} />
        <Route path="/catalogue"   element={<CataloguePage />} />
        <Route path="/packs"       element={<PacksPage />} />
        <Route path="/compliance"  element={<CompliancePage />} />
        <Route path="/compliance/deletions/:id" element={<DeletionDetailPage />} />
        <Route path="/ops"         element={<OpsPage />} />
        <Route path="/psychometric/authoring" element={<PsychometricAuthoringPage />} />
        {/* Retired 29 Oct: signup sources and activation now live in Growth; the v3.5 launch monitor and the cohort comparison are gone. */}
        <Route path="/signup-sources" element={<Navigate to="/growth" replace />} />
        <Route path="/activation"  element={<Navigate to="/growth" replace />} />
        <Route path="/launch"      element={<Navigate to="/" replace />} />
        <Route path="/comparison"  element={<Navigate to="/growth" replace />} />
        <Route path="*"            element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
};

export const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthGate>
        <InnerApp />
      </AuthGate>
    </BrowserRouter>
  </QueryClientProvider>
);
